// components/EmailTemplatePopup.js
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import {
  createNewEmailTemplate,
  fetchAllEmailTemplates,
  saveCustomEmailTemplate,
  updateCustomEmailTemplate,
} from "../Redux/ReduxSlices/EmailTemplateSlice";
import { useAppContext } from "../context/AppContext";

const EmailTemplatePopup = ({
  isOpen,
  onClose,
  onSuccess,
  existingTemplate = null, // For edit mode
}) => {
  const dispatch = useDispatch();
  const { promptInput, promptsDataCompany, promptsDataPeople, viewMode } =
    useAppContext();
  const isEditMode = !!existingTemplate;
  // Form state
  const [newTemplateData, setNewTemplateData] = useState({
    subject_data: "",
    body_data: "",
    footer_data: "",
  });

  // AI state
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiWebsiteUrl, setAiWebsiteUrl] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sendPromptInput, setSendPromptInput] = useState(true);

  useEffect(() => {
    if (viewMode == "companies" && promptsDataCompany == null) {
      setSendPromptInput(false);
    } else if (viewMode == "people" && promptsDataPeople.data.length == 0) {
      setSendPromptInput(false);
    } else {
      setSendPromptInput(true);
    }
  }, []);

  // Helper: strip HTML tags for plain-text display
  const stripHtml = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html || "", "text/html");
      return (doc.body.textContent || "").replace(/\u00A0/g, " ").trim();
    } catch {
      return (html || "").replace(/<[^>]*>/g, "").trim();
    }
  };

  // Load existing template data if in edit mode
  useEffect(() => {
    if (isEditMode && existingTemplate) {
      setNewTemplateData({
        subject_data: stripHtml(existingTemplate.subject_data || ""),
        body_data: stripHtml(existingTemplate.body_data || ""),
        footer_data: stripHtml(existingTemplate.footer_data || ""),
      });
      // Pre-fill AI prompt with existing template content for regeneration
      setAiPrompt(
        stripHtml(existingTemplate.body_data || "").substring(0, 200),
      );
    } else {
      // Reset form when opening in create mode
      setNewTemplateData({
        subject_data: "",
        body_data: "",
        footer_data: "",
      });
      setAiPrompt("");
      setAiWebsiteUrl("");
      setAiGenerating(false);
    }
  }, [isEditMode, existingTemplate, isOpen]);

  const canCreate = useMemo(() => {
    return (
      newTemplateData.subject_data.trim() &&
      newTemplateData.body_data.trim() &&
      newTemplateData.footer_data.trim()
    );
  }, [newTemplateData]);

  const hasGenerated = useMemo(() => {
    return Boolean(
      (newTemplateData.subject_data && newTemplateData.subject_data.trim()) ||
      (newTemplateData.body_data && newTemplateData.body_data.trim()) ||
      (newTemplateData.footer_data && newTemplateData.footer_data.trim()),
    );
  }, [newTemplateData]);

  const handleNewTemplateChange = (field, value) => {
    setNewTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  // Generate email with AI using backend API
  const handleGenerateTemplateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a prompt for AI template generation");
      return;
    }

    try {
      setAiGenerating(true);

      let finalPrompt = aiPrompt.trim();
      if (aiWebsiteUrl.trim()) {
        finalPrompt = `${aiPrompt.trim()} please review our website: ${aiWebsiteUrl.trim()}. Use the business model, services, and brand voice to create a well-structured, professional, and conversion-driven email that resonates with the target audience and encourages action;`;
      }

      const payload = {
        prompt: finalPrompt,
        user_prompt: sendPromptInput ? promptInput : "",
      };

      const res = await dispatch(createNewEmailTemplate(payload)).unwrap();
      const tpl = res?.data || res || {};

      // Store sanitized plain text (NO TAGS)
      setNewTemplateData({
        subject_data: stripHtml(tpl.subject_data || ""),
        body_data: stripHtml(tpl.body_data || ""),
        footer_data: stripHtml(tpl.footer_data || ""),
      });

      toast.success("AI email template generated successfully");
    } catch (err) {
      toast.error("Failed to generate template with AI ❌");
    } finally {
      setAiGenerating(false);
    }
  };

  // Create template
  const handleCreateTemplate = async () => {
    if (!newTemplateData.subject_data.trim()) {
      toast.error("Subject is required");
      return Promise.reject("subject-required");
    }

    setLoading(true);
    try {
      await dispatch(saveCustomEmailTemplate(newTemplateData)).unwrap();
      toast.success("Email template created successfully!");
      dispatch(fetchAllEmailTemplates());

      // Reset form
      setNewTemplateData({
        subject_data: "",
        body_data: "",
        footer_data: "",
      });
      setAiPrompt("");
      setAiWebsiteUrl("");
      setShowConfirmModal(false);

      // Close popup
      onClose();

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error("Failed to create email template");
      console.error("Create failed:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update template (for edit mode)
  const handleUpdateTemplate = async () => {
    if (!newTemplateData.subject_data.trim()) {
      toast.error("Subject is required");
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        updateCustomEmailTemplate({
          id: existingTemplate.id,
          payload: newTemplateData,
        }),
      ).unwrap();
      toast.success("Email template updated successfully!");
      dispatch(fetchAllEmailTemplates());

      // Close popup
      onClose();

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error("Failed to update email template");
      console.error("Update failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (isEditMode) {
      handleUpdateTemplate();
    } else {
      setShowConfirmModal(true);
    }
  };

  return (
    <>
      {/* Main Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] p-0">
          <div className="flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="p-6 border-b">
              <DialogHeader>
                <DialogTitle>
                  {isEditMode
                    ? "Edit Email Template"
                    : "Create New Email Template"}
                </DialogTitle>
                <DialogDescription className="text-gray-700">
                  {isEditMode
                    ? "Edit your email template content. Changes will affect all campaigns using this template."
                    : "Generate with AI, edit the content, then save your template."}
                </DialogDescription>
              </DialogHeader>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Only show AI generation section for create mode */}
              {!isEditMode && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Enter your business website (optional)
                    </label>
                    <input
                      value={aiWebsiteUrl}
                      onChange={(e) => setAiWebsiteUrl(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm bg-white text-black focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      placeholder="Enter your Website"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Provide some details about your Business
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm bg-white text-black 
                        focus:border-blue-500 focus:ring-1 focus:ring-blue-500 
                        transition-all resize-none"
                      placeholder={
                        "Examples:\n1. We manufacture and distribute food products.\n2. We provide IT talent to IT companies"
                      }
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleGenerateTemplateWithAI}
                      disabled={aiGenerating || !aiPrompt.trim()}
                      className={`px-4 sm:px-5 py-2 rounded-md main-button text-white focus:outline-none focus:ring-2 focus:ring-blue-500
                        flex items-center justify-center min-w-[160px] transition-all ${
                          aiGenerating || !aiPrompt.trim()
                            ? "opacity-70 cursor-not-allowed"
                            : ""
                        }`}
                    >
                      {aiGenerating ? (
                        <>
                          <Loader size={18} className="animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        "Generate Email with AI"
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Email Content Editor */}
              {(hasGenerated || isEditMode) && (
                <div className="mt-4 border rounded-md p-4 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    {isEditMode ? "Edit Email Content" : "Edit Email Content"}
                  </h3>

                  <div className="space-y-4">
                    {/* Subject */}
                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={newTemplateData.subject_data}
                        onChange={(e) =>
                          handleNewTemplateChange(
                            "subject_data",
                            e.target.value,
                          )
                        }
                        className="mt-1 w-full p-2 border rounded bg-white text-black focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter subject"
                      />
                    </div>

                    {/* Body */}
                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        Body <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={10}
                        value={newTemplateData.body_data}
                        onChange={(e) =>
                          handleNewTemplateChange("body_data", e.target.value)
                        }
                        className="mt-1 w-full p-2 border rounded bg-white text-black whitespace-pre-wrap focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter email body"
                      />
                    </div>

                    {/* Footer */}
                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        Footer <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={3}
                        value={newTemplateData.footer_data}
                        onChange={(e) =>
                          handleNewTemplateChange("footer_data", e.target.value)
                        }
                        className="mt-1 w-full p-2 border rounded bg-white text-black whitespace-pre-wrap focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter footer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {!isEditMode && canCreate && (
                <div className="text-xs text-gray-600">
                  AI generated content ready — click <b>Save Template</b> to
                  continue.
                </div>
              )}
            </div>

            {/* Sticky Footer Buttons */}
            <div className="p-6 border-t flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button
                onClick={onClose}
                className="px-3 py-2 rounded-md bg-white text-black shadow-lg border hover:bg-gray-200 transition-colors w-full sm:w-auto"
                disabled={loading}
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={loading || !canCreate}
                className={`px-3 py-2 rounded-md shadow-lg flex items-center justify-center min-w-[120px] w-full sm:w-auto transition-all ${
                  canCreate
                    ? "main-button text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
              >
                {loading ? (
                  <Loader size={18} className="animate-spin text-white" />
                ) : isEditMode ? (
                  "Update Template"
                ) : (
                  "Save Template"
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Create Dialog (only for create mode) */}
      {!isEditMode && (
        <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Create New Template</DialogTitle>
              <DialogDescription className="text-gray-700">
                Are you sure you want to create this new email template?
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="flex justify-end gap-3 sm:justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-5 py-2.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>

              <button
                onClick={handleCreateTemplate}
                disabled={loading}
                className="px-5 py-2.5 rounded-md main-button shadow-lg text-white flex items-center justify-center min-w-[90px]"
              >
                {loading ? (
                  <Loader size={18} className="animate-spin text-white" />
                ) : (
                  "Confirm"
                )}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default EmailTemplatePopup;
